import React, { useRef, Fragment } from 'react'
import { useSelector } from 'react-redux'

import { medicationRequest } from '@enlyt/stores/Fhir/Select'

import { useReactToPrint } from 'react-to-print'

import { styles, themeStyles } from './styles'
import localization from './localization'

import { useTheme } from 'contexts/ThemeProvider'

import PrintFooter from 'containers/MyHealth/components/PrintFooter'
import PrintHeader from 'containers/MyHealth/components/PrintHeader'
import Divider from 'containers/MyHealth/components/Divider'

import { Space, Layout } from 'antd'
import { PrinterFilled } from '@ant-design/icons'
import Bowser from 'bowser'

import _ from 'lodash'
import moment from 'moment'
import { GridRow, GridColumn, Grid, Button } from 'semantic-ui-react'
import withLocalization from '@web/helpers/localizationUtils'

const printPageStyle = `
@media print {
  @page {
    margin-top: 0;
    -webkit-margin-top: 0;
    margin-bottom: 0;
    -webkit-margin-top: 0;
  }
}
body {
  -webkit-print-color-adjust: exact;
  background:#fff !important;
  break-inside: auto !important;
}
.print{
  margin: 0 !important;
  padding: 0;
  width: auto !important;
  height: auto !important;
  z-index: 10000 !important;
}
`

const BROWSER_PAGINATION_LIMIT = {
  safari: 500,
  iOSNonSafari: 550,
  others: 750
}

const getPaginationLimitByBrowser = (isIos = false, isSafari = false) => {
  if (!isIos && !isSafari) {
    return BROWSER_PAGINATION_LIMIT.others
  }
  if (isSafari) {
    return BROWSER_PAGINATION_LIMIT.safari
  }
  return BROWSER_PAGINATION_LIMIT.iOSNonSafari
}

const getLayoutStyle = (isSafari = false, isIOS = false) => {
  if (isSafari) {
    return styles.printLayoutSafari
  }
  if (isIOS) {
    return styles.printLayoutInIos
  }
  return styles.printLayout
}

// Segment the input array, take a specific number of elements and construct a two-dimensional array.
function formatList(
  arr = [],
  maxPageHeight = 650,
  maxWidth = 245,
  fontSize = '16px'
) {
  arr = _.isArray(arr) ? arr : []

  const result = []
  let currentPage = []
  let currentPageHeight = 0

  arr.forEach(item => {
    const title = _.get(item, 'resource.medicationCodeableConcept.text', '')
    const content = _.get(
      item,
      'resource.dosageInstruction[0].patientInstruction',
      ''
    )

    const titleHeight = calculateLines(title, maxWidth, fontSize)
    const contentHeight = calculateLines(content, maxWidth, fontSize)
    const orderBy =
      _.get(item, 'resource.requester.display') ||
      _.get(item, 'resource.recorder.display', '')
    const orderByHeight = calculateLines(orderBy, 101, fontSize)
    const itemHeight = Math.max(titleHeight, contentHeight, orderByHeight) + 80 // 80 is padding top + padding right
    const maxCommonHeight =
      result.length === 0 ? maxPageHeight : maxPageHeight + 100 // 100 is the padding top only needed in first page
    if (
      currentPageHeight + itemHeight > maxCommonHeight &&
      currentPage.length > 0
    ) {
      result.push(currentPage)
      currentPage = []
      currentPageHeight = 0
    }

    currentPage.push(item)
    currentPageHeight += itemHeight
  })

  if (currentPage.length > 0) {
    result.push(currentPage)
  }

  return result
}

function calculateLines(text, width, fontSize) {
  const div = document.createElement('div')
  div.style.position = 'absolute'
  div.style.visibility = 'hidden'
  div.style.width = `${width}px`
  div.style.fontSize = fontSize
  div.style.whiteSpace = 'pre-wrap'
  div.style.wordWrap = 'break-word'

  document.body.appendChild(div)
  div.innerText = text

  const height = div.clientHeight
  document.body.removeChild(div)

  return height
}

const MedicationItem = props => {
  const isShowHeader = _.get(props, 'isShowHeader', false)
  const { t } = props
  const headerRow = {
    marginBottom: isShowHeader && 15
  }
  return (
    <div className='medication-print-item' style={styles.medicationPrintItem}>
      <Grid style={headerRow}>
        <GridRow>
          {isShowHeader && (
            <>
              <GridColumn width={5} style={styles.header}>
                <div style={styles.medicationFieldName}>
                  {t('medicationUppercase')}
                </div>
              </GridColumn>
              <GridColumn width={3} style={styles.header}>
                <div style={{ ...styles.fieldName, ...styles.fieldNameNoWrap }}>
                  {t('startedOnUppercase')}
                </div>
              </GridColumn>
              <GridColumn width={3} style={styles.header}>
                <div style={styles.fieldName}>{t('orderedByUppercase')}</div>
              </GridColumn>
              <GridColumn width={5} style={styles.header}>
                <div style={styles.fieldName}>{t('instructionsUppercase')}</div>
              </GridColumn>
            </>
          )}
        </GridRow>
      </Grid>
      <Grid>
        <GridRow style={styles.noPaddingTop}>
          <GridColumn width={5}>
            <div style={styles.medicationName}>
              <strong>
                {_.get(props, 'resource.medicationCodeableConcept.text')}
              </strong>
            </div>
          </GridColumn>
          <GridColumn width={3}>
            <div style={styles.itemContent}>
              {moment(_.get(props, 'resource.authoredOn')).format('M/D/YYYY')}
            </div>
          </GridColumn>
          <GridColumn width={3}>
            <div style={styles.itemContent}>
              {_.get(props, 'resource.requester.display')}
            </div>
          </GridColumn>
          <GridColumn width={5}>
            <div style={styles.itemContent}>
              {_.get(props, 'resource.dosageInstruction[0].patientInstruction')}
            </div>
          </GridColumn>
        </GridRow>
      </Grid>
    </div>
  )
}

const MedicationPrintLayout = props => {
  const { Header, Footer, Content } = Layout
  const t = _.get(props, 't', null)
  const showHeader = _.get(props, 'showHeader', false)
  const items = _.get(props, 'item', [])
  const totalPage = _.get(props, 'totalPage', 0)
  const curPage = _.get(props, 'curPage', 0)
  const userAgent = navigator.userAgent
  const browser = Bowser.getParser(userAgent)
  const isSafari = browser.getBrowserName() === 'Safari'
  const os = browser.getOS()
  const isIOS = os.name === 'iOS'
  const layoutStyles = getLayoutStyle(isSafari, isIOS)
  return (
    <div>
      <Layout style={layoutStyles}>
        <Header
          style={
            curPage === 1
              ? styles.printLayoutHeader
              : styles.printLayoutCommonHeader
          }
        >
          <PrintHeader title='medication' showHeader={showHeader} />
        </Header>
        <Content style={styles.printLayoutContent}>
          {items.map((item, index) => {
            return (
              <Fragment key={index}>
                <MedicationItem {...item} isShowHeader={index === 0} t={t} />
                {index === items.length - 1 ? null : <Divider />}
              </Fragment>
            )
          })}
        </Content>
        <Footer style={styles.printLayoutFooter}>
          <PrintFooter
            totalPage={totalPage}
            curPage={curPage}
            id='medication'
          />
        </Footer>
      </Layout>
    </div>
  )
}

const MedicationsPrintContext = props => {
  const medications = _.get(props, 'medications', [])
  const t = _.get(props, 't', null)

  return (
    <div className='medication-print-container'>
      {medications.map((item, index) => {
        return (
          <MedicationPrintLayout
            key={index}
            item={item}
            totalPage={medications.length}
            curPage={index + 1}
            showHeader={index === 0}
            t={t}
          />
        )
      })}
    </div>
  )
}

const MedicationsPrintModal = ({ t }) => {
  const { theme } = useTheme()
  const buttonStyles = themeStyles(theme)
  const printRef = useRef(null)

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: printPageStyle,
    removeAfterPrint: false
  })

  const userAgent = navigator.userAgent
  const browser = Bowser.getParser(userAgent)
  const os = browser.getOS()
  const isIOS = os.name === 'iOS'
  const isSafari = browser.getBrowserName() === 'Safari'

  const paginationLimit = getPaginationLimitByBrowser(isIOS, isSafari)
  const medications = formatList(
    _.get(useSelector(medicationRequest), 'entry', []),
    paginationLimit,
    194,
    '14px'
  )

  return (
    <Fragment>
      <div
        className='print'
        ref={printRef}
        style={styles.printContainer}
        id='medications-print-area'
      >
        <MedicationsPrintContext medications={medications} t={t} />
      </div>
      {!_.isEmpty(medications) && (
        <Button style={buttonStyles.downLoadButton} onClick={handlePrint}>
          <Space>
            <PrinterFilled style={styles.printerIcon} />
            <span>{t('print')}</span>
          </Space>
        </Button>
      )}
    </Fragment>
  )
}

export default withLocalization(MedicationsPrintModal, localization)
